import type { ReactNode } from 'react'
import { NavLink } from 'react-router-dom'

import { logout } from '../api/auth'
import { useTenant } from '../api/tenant'
import type { Session } from '../api/types'

/**
 * The frame every screen sits in.
 *
 * The skip link is first in the DOM on purpose: a keyboard user should not have to
 * pass the whole navigation to reach the queue, and this application is meant to be
 * driven by keyboard.
 */
export function Shell({ session, children }: { session: Session; children: ReactNode }) {
  const slugs = session.memberships.map((membership) => membership.tenant)
  const [tenant, chooseTenant] = useTenant(slugs)

  return (
    <div className="app">
      <a className="skip-link" href="#main">
        Skip to content
      </a>
      <div className="sidebar">
        <h1>Sentry</h1>
        <nav aria-label="Sections">
          <NavLink to="/overview">Overview</NavLink>
          <NavLink to="/dashboard">Dashboard</NavLink>
          <NavLink to="/queue">Triage</NavLink>
          <NavLink to="/hunt">Hunt</NavLink>
          <NavLink to="/rules">Rules</NavLink>
          <NavLink to="/response">Response</NavLink>
          <NavLink to="/coverage">Coverage</NavLink>
          <NavLink to="/inventory">Inventory</NavLink>
          <NavLink to="/vulnerabilities">Vulnerabilities</NavLink>
          <NavLink to="/compliance">Compliance</NavLink>
          <NavLink to="/reports">Reports</NavLink>
          <NavLink to="/admin">Administration</NavLink>
          <NavLink to="/guide">Qo&apos;llanma</NavLink>
        </nav>
        <div style={{ marginTop: 'auto' }} className="muted">
          {/* Always visible, even with one tenant. A console that shows whose data it
              is only when it happens to be ambiguous is a console that will be
              misread on the day it matters. */}
          <label htmlFor="tenant">Tenant</label>
          {slugs.length > 1 ? (
            <select
              id="tenant"
              value={tenant}
              onChange={(event) => chooseTenant(event.target.value)}
              style={{ width: '100%', marginBottom: 'var(--space-3)' }}
            >
              {session.memberships.map((membership) => (
                <option key={membership.tenant} value={membership.tenant}>
                  {membership.tenant_name}
                </option>
              ))}
            </select>
          ) : (
            <p className="mono" id="tenant" style={{ marginTop: 0 }}>
              {tenant || 'no tenant'}
            </p>
          )}
          <p style={{ marginBottom: 'var(--space-2)' }}>{session.username}</p>
          <button
            onClick={async () => {
              await logout()
              window.location.reload()
            }}
          >
            Sign out
          </button>
        </div>
      </div>
      <main id="main">{children}</main>
    </div>
  )
}
